import React, { useRef, useEffect } from "react";
import {
  Drawer,
  Box,
  Tooltip,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Typography,
} from "@mui/material";
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import CloseIcon from "@mui/icons-material/Close";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { MaskGlyph, BoltGlyph } from "./QuickChatGlyphs";
import QC from "./quickChatTheme";
import useQuickChatIntro from "../hooks/useQuickChatIntro";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import useMediaQuery from "@material-ui/core/useMediaQuery";

const useStyles = makeStyles((theme) => ({
  chatSessionText: {
    fontFamily: "Raleway",
    fontSize: "0.9em",
    fontWeight: "normal",
    padding: "0.5px 3px",
  },
}));

const ChatSidebar = ({
  open,
  onClose,
  onNewChat,
  chatSessions,
  activeChatId,
  onChatSelection,
  selectedChatForMenu,
  onMouseEnter,
  onMenuOpen,
  onStartQuickChat,
  quickChatActive = false,
}) => {
  const { seen: quickChatIntroSeen, markSeen: dismissQuickChatIntro } = useQuickChatIntro();
  const showIntroCallout = !!onStartQuickChat && !quickChatIntroSeen;

  // Auto-dismiss the callout when the user closes the sidebar after having
  // had it open. They had their chance to read; don't make them hunt for X.
  const prevOpenRef = useRef(open);
  useEffect(() => {
    if (onStartQuickChat && prevOpenRef.current && !open) {
      dismissQuickChatIntro();
    }
    prevOpenRef.current = open;
  }, [open, onStartQuickChat, dismissQuickChatIntro]);

  // Clicking the pinned row counts as discovering the feature too.
  const handlePinnedRowClick = () => {
    dismissQuickChatIntro();
    onStartQuickChat?.();
  };
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const listRef = useRef(null);
  // Store the chat positions by ID
  const chatPositions = useRef({});

  // Store selection information
  const storePosition = (chatId) => {
    if (listRef.current) {
      // Find the ListItem element by chatId
      const listItem = listRef.current.querySelector(`[data-chat-id="${chatId}"]`);
      if (listItem) {
        const listRect = listRef.current.getBoundingClientRect();
        const itemRect = listItem.getBoundingClientRect();
        
        // Calculate relative position within the list
        const relativePosition = itemRect.top - listRect.top;
        
        // Store position and scroll data
        chatPositions.current = {
          chatId,
          relativePosition,
          scrollTop: listRef.current.scrollTop
        };
      }
    }
  };

  const handleChatSelection = (chatId) => {
    storePosition(chatId);
    onChatSelection(chatId);
  };
  
  // When the drawer opens, restore the position
  useEffect(() => {
    if (open && activeChatId && chatPositions.current.chatId === activeChatId) {
      setTimeout(() => {
        if (listRef.current) {
          // Find the currently active chat element
          const listItem = listRef.current.querySelector(`[data-chat-id="${activeChatId}"]`);
          if (listItem) {
            const listRect = listRef.current.getBoundingClientRect();
            const itemRect = listItem.getBoundingClientRect();
            const currentRelativePos = itemRect.top - listRect.top;
            
            // Calculate how much we need to adjust the scroll to maintain the same relative position
            const adjustment = currentRelativePos - chatPositions.current.relativePosition;
            
            // Set the scroll position
            listRef.current.scrollTop = chatPositions.current.scrollTop;
          }
        }
      }, 100);
    }
  }, [open, activeChatId]);

  return (
    <Drawer
      anchor="left"
      open={open}
      onClose={onClose}
      PaperProps={{
        style: {
          backgroundColor: "#191919",
          color: "white",
          width: isMobile ? 280 : 320,
          height: "100vh",
          transition: theme.transitions.create("width", {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
        },
      }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", height: "100%", overflow: "hidden" }} data-tour="chat-sidebar">
        <Box style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexShrink: 0,
          padding: "12px 16px 8px",
          borderBottom: "1px solid rgba(255,255,255,0.08)",
        }}>
          <Typography style={{
            color: "#FFC629",
            fontFamily: "Raleway, sans-serif",
            fontWeight: "bold",
            fontSize: "1.2rem",
            letterSpacing: "0.03em",
          }}>
            Chat History
          </Typography>
          <Box style={{ display: "flex", gap: 6 }}>
            <Tooltip title="New chat">
              <IconButton
                aria-label="New chat"
                onClick={onNewChat}
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: "6px",
                  border: `1px solid ${QC.border}`,
                  color: "#FFC629",
                  padding: 0,
                  "&:hover": { backgroundColor: "rgba(255,198,41,0.08)" },
                }}
              >
                <AddRoundedIcon style={{ fontSize: "1.4rem" }} />
              </IconButton>
            </Tooltip>
            {onStartQuickChat && (
              <Tooltip title="New quick chat">
                <IconButton
                  aria-label="New quick chat"
                  onClick={onStartQuickChat}
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: "6px",
                    border: `1px dashed ${QC.borderDash}`,
                    backgroundColor: "#1a1410",
                    color: QC.fgMid,
                    padding: 0,
                    "&:hover": { backgroundColor: "#241c16" },
                  }}
                >
                  <MaskGlyph size={20} color={QC.fgMid} />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title="Close Sidebar">
              <IconButton onClick={onClose} sx={{ color: "rgba(255,255,255,0.5)" }}>
                <CloseIcon style={{ fontSize: "1.6rem" }} />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {onStartQuickChat && (
          <Box
            data-testid="quick-chat-pinned-row"
            data-tour="quick-chat-pinned-row"
            onClick={handlePinnedRowClick}
            role="button"
            tabIndex={0}
            aria-pressed={quickChatActive}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                handlePinnedRowClick();
              }
            }}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "10px",
              margin: "8px 12px",
              padding: "12px",
              borderRadius: "8px",
              cursor: "pointer",
              border: `1px dashed ${QC.borderDash}`,
              background: "linear-gradient(180deg,#1a1410,#13100c)",
              boxShadow: quickChatActive
                ? `inset 0 0 0 1px ${QC.borderDash}`
                : "none",
              "&:hover": {
                borderColor: "#6a5840",
              },
            }}
          >
            <MaskGlyph size={20} color={QC.fgMid} />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                sx={{
                  color: QC.fg,
                  fontFamily: "Raleway, sans-serif",
                  fontWeight: 600,
                  fontSize: "13px",
                }}
              >
                Start quick chat
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                  marginTop: "2px",
                }}
              >
                <BoltGlyph size={9} color={QC.accentBolt} />
                <Typography
                  component="span"
                  sx={{
                    color: QC.fgMute,
                    fontFamily: "Poppins, sans-serif",
                    fontSize: "10.5px",
                  }}
                >
                  Faster · not saved
                </Typography>
              </Box>
            </Box>
          </Box>
        )}

        {showIntroCallout && (
          <Box
            data-testid="quick-chat-intro-callout"
            sx={{
              position: "relative",
              margin: "4px 12px 12px",
              padding: "12px 14px",
              backgroundColor: "rgba(255,198,41,0.08)",
              border: "1px solid rgba(255,198,41,0.35)",
              borderRadius: "8px",
              // Arrow pointing up at the pinned row above
              "&::before": {
                content: '""',
                position: "absolute",
                top: "-7px",
                left: "26px",
                width: "12px",
                height: "12px",
                backgroundColor: "rgba(255,198,41,0.08)",
                borderLeft: "1px solid rgba(255,198,41,0.35)",
                borderTop: "1px solid rgba(255,198,41,0.35)",
                transform: "rotate(45deg)",
              },
            }}
          >
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 1 }}>
              <Typography
                sx={{
                  color: "#FFC629",
                  fontFamily: "Raleway, sans-serif",
                  fontWeight: 700,
                  fontSize: "11px",
                  letterSpacing: "0.08em",
                  textTransform: "uppercase",
                }}
              >
                New
              </Typography>
              <IconButton
                aria-label="Dismiss quick chat intro"
                onClick={dismissQuickChatIntro}
                size="small"
                sx={{ color: "rgba(255,255,255,0.55)", padding: "2px", marginTop: "-4px", marginRight: "-4px" }}
              >
                <CloseRoundedIcon fontSize="inherit" />
              </IconButton>
            </Box>
            <Typography
              sx={{
                color: QC.fg,
                fontFamily: "Raleway, sans-serif",
                fontWeight: 600,
                fontSize: "13px",
                marginTop: "2px",
              }}
            >
              Quick chat is here
            </Typography>
            <Typography
              sx={{
                color: QC.fgSub,
                fontFamily: "Poppins, sans-serif",
                fontSize: "11.5px",
                lineHeight: 1.45,
                marginTop: "4px",
              }}
            >
              A faster, lighter chat for one-off questions. Nothing is saved to your history.
            </Typography>
            <Box sx={{ display: "flex", justifyContent: "flex-end", marginTop: "8px" }}>
              <Box
                component="button"
                onClick={dismissQuickChatIntro}
                sx={{
                  background: "transparent",
                  border: "none",
                  cursor: "pointer",
                  color: "#FFC629",
                  fontFamily: "Raleway, sans-serif",
                  fontWeight: 700,
                  fontSize: "11.5px",
                  padding: "2px 4px",
                }}
              >
                Got it
              </Box>
            </Box>
          </Box>
        )}

        {onStartQuickChat && (
          <>
            <Box sx={{ height: "1px", backgroundColor: QC.borderSoft, marginX: "12px" }} />
            <Typography
              sx={{
                color: QC.fgFaint,
                fontFamily: "Poppins, sans-serif",
                fontSize: "10.5px",
                fontWeight: 700,
                letterSpacing: "1.2px",
                textTransform: "uppercase",
                padding: "10px 16px 4px",
              }}
            >
              Recent
            </Typography>
          </>
        )}

        <List
          ref={listRef}
          sx={{
            flex: 1,
            overflowY: "auto",
            scrollbarWidth: "thin",
            scrollbarColor: "rgba(255,255,255,0.08) transparent",
            "&::-webkit-scrollbar": { width: "4px" },
            "&::-webkit-scrollbar-track": { background: "transparent" },
            "&::-webkit-scrollbar-thumb": {
              background: "rgba(255,255,255,0.08)",
              borderRadius: "2px",
            },
            "&::-webkit-scrollbar-thumb:hover": {
              background: "rgba(255,255,255,0.15)",
            },
          }}
        >
          {chatSessions.length === 0 ? (
            <ListItem>
              <ListItemText
                primary="No chats available"
                secondary="Start a new chat to see it here."
                primaryTypographyProps={{
                  style: {
                    fontFamily: "Raleway, sans-serif",
                    color: "white",
                  },
                }}
                secondaryTypographyProps={{
                  style: {
                    fontFamily: "Raleway, sans-serif",
                    color: "#a9a9a9",
                  },
                }}
              />
            </ListItem>
          ) : (
            chatSessions.map((chat, index) => (
              <ListItem
                button
                key={index}
                data-chat-id={chat.id}
                onClick={() => handleChatSelection(chat.id)}
                onMouseEnter={() => onMouseEnter(chat)}
                style={{
                  backgroundColor:
                    activeChatId === chat.id ? "#3f3f3f" : "transparent",
                  borderRadius: 7,
                  justifyContent: "space-between",
                  alignItems: "center",
                  margin: "2px 8px",
                  width: "auto",
                  borderBottom: "1px solid rgba(255,255,255,0.06)",
                }}
              >
                <ListItemText
                  primary= {chat.chatName}
                  primaryTypographyProps={{
                    style: {
                      fontFamily: "Poppins, sans-serif",
                      color: "white",
                      fontSize: "1rem",
                      fontWeight: "lighter",
                      ellipsis: "true",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      maxWidth: "100%",
                    },
                  }}
                />
                <IconButton
                  style={{
                    color: "#808080a6",
                    visibility:
                      selectedChatForMenu?.id === chat.id ? "visible" : "hidden",
                  }}
                  onClick={(e) => onMenuOpen(e, chat)}
                >
                  <MoreHorizIcon fontSize="small" />
                </IconButton>
              </ListItem>
            ))
          )}
        </List>
      </Box>
    </Drawer>
  );
};

export default ChatSidebar; 